// Held weapons. Each is authored pointing RIGHT with the grip (where the
// front fist closes) at the local origin; the barrel axis runs a few px above
// the grip. Silhouettes are deliberately distinct at gameplay size:
//   Pulse Carbine    compact rifle, three glowing pulse coils
//   Scatterforge     chunky wide bore, heat fins on the receiver
//   Arc Lance        twin-pronged emitter wrapped in coil rings
//   Rail Driver      long twin rails over a capacitor bank
//   Micro-Rocket Rack boxy launcher, 2×3 tube face
//   Gravity Launcher bulbous chamber with a violet singularity core

import { COLORS } from '../../config/palette';
import type { WeaponId } from '../../core/types';
import type { Drawer } from './draw';
import type { Bounds } from './sprite';
import type { MechPalette, Ramp } from './styles';

export interface WeaponSpec {
  bounds: Bounds;
  muzzle: { x: number; y: number };
  foregrip: { x: number; y: number };
  /** Optional front-hand reach override (see WeaponArt.holdReach). */
  holdReach?: number;
  /** Optional front-hand drop below the aim line (see WeaponArt.holdDrop). */
  holdDrop?: number;
  glowColor: number;
  accent: Ramp;
  draw(d: Drawer): void;
}

const ramp = (shadow: number, base: number, light: number, edge: number): Ramp => ({ shadow, base, light, edge });

/** Shared gun-metal materials; accent paint and emissive colour vary per weapon. */
export function weaponPalette(spec: WeaponSpec): MechPalette {
  return {
    mats: {
      armor: ramp(0x3b4553, 0x5f6c7e, 0x8795a8, 0xc3cedb),
      plate: ramp(0x1d232c, 0x2f3743, 0x47515f, 0x76818f),
      accent: spec.accent,
      frame: ramp(0x0f1217, 0x1b2028, 0x2b323d, 0x4a5361),
      metal: ramp(0x3a3f47, 0x646b75, 0x9aa2ad, 0xdde3ea),
    },
    glass: 0x0b1820,
    reactor: spec.glowColor,
  };
}

/** Pistol grip hanging below the receiver (the fist wraps it). */
function grip(d: Drawer): void {
  d.plate([[-2.6, -2.5], [2.4, -2.5], [1.4, 6], [-3.2, 6.6]], 'frame', { sh: 1, hl: 0.6 });
  d.panel([[-2.4, 1.5], [1.8, 1.5]], 0.5);
}

const ring = (d: Drawer, x: number, y0: number, y1: number, w: number, k: number): void => {
  d.plate([[x, y0], [x + w, y0], [x + w, y1], [x, y1]], 'frame', { sh: 0.6, hl: 0.5, ink: 0.6, r: 0.4 });
  d.strip([[x + w / 2, y0 + 1.2], [x + w / 2, y1 - 1.2]], 0.9, k);
};

const SPECS: Record<WeaponId, WeaponSpec> = {
  pulse_carbine: {
    bounds: { x0: -17, y0: -12.5, x1: 42, y1: 7 },
    muzzle: { x: 41.5, y: -5 },
    foregrip: { x: 15, y: -0.5 },
    glowColor: COLORS.volt,
    accent: ramp(0x18456d, 0x2a6fae, 0x55a0df, 0xa3d6ff),
    draw(d) {
      d.plate([[-15.5, -6.6], [-4, -7], [-3, -1.5], [-8, -0.5], [-15, 2.8], [-16.5, -2]], 'plate', { sh: 1.4, hl: 0.8 });
      d.slot([[-12.5, -3.8], [-7, -4.2], [-7.6, -2], [-12, -0.6]]);
      grip(d);
      d.plate([[6, -1.2], [11, -1.2], [10, 4.5], [6.6, 4.5]], 'plate', { sh: 1, hl: 0.6 });
      d.plate([[17, -8.2], [34, -7.6], [34, -2.6], [17, -2]], 'plate', { sh: 1.2, hl: 0.7 });
      d.plate([[-5.5, -9.2], [14, -9.6], [18.5, -7.2], [18.5, -1.2], [-4, -0.6], [-6.5, -4.4]], 'armor', { sh: 2.4, hl: 1, r: 0.6 });
      d.edge([[-5.2, -9], [14, -9.4]]);
      d.plate([[-4.6, -7.6], [9, -7.9], [10, -5.9], [-4.4, -5.6]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
      d.panel([[11, -9.2], [11.5, -1]]);
      ring(d, 20.6, -9.6, -0.8, 2.6, 1);
      ring(d, 25.2, -9.4, -1, 2.6, 0.95);
      ring(d, 29.8, -9.2, -1.2, 2.6, 0.9);
      d.plate([[34, -7.2], [40.2, -6.8], [41.6, -5.6], [41.6, -4.2], [40.2, -3.2], [34, -3]], 'metal', { sh: 1, hl: 0.6 });
      d.plate([[2, -9.3], [8.4, -9.4], [7.4, -11.8], [3, -11.8]], 'frame', { sh: 0.5, hl: 0.5, ink: 0.6 });
      d.lampDisc(6.4, -10.6, 0.8, 0.9, 0.3);
    },
  },

  scatterforge: {
    bounds: { x0: -14, y0: -18.5, x1: 48.5, y1: 8 },
    muzzle: { x: 47.5, y: -5.5 },
    foregrip: { x: 22, y: 2.5 },
    glowColor: COLORS.burn,
    accent: ramp(0x6b3a12, 0xa85a1c, 0xd9843a, 0xffbf80),
    draw(d) {
      d.plate([[-13.5, -8.6], [-4, -9.4], [-3, -1.5], [-12.5, 1.2]], 'plate', { sh: 1.6, hl: 0.8 });
      grip(d);
      // Heat fins (behind the receiver top edge).
      for (const x of [5, 9.5, 14, 18.5]) {
        d.plate([[x, -12], [x + 2.4, -12], [x + 3, -17.2], [x + 0.6, -17.4]], 'metal', { sh: 0.7, hl: 0.5, ink: 0.6, r: 0.3 });
        d.strip([[x + 1.3, -12.6], [x + 1.8, -16.4]], 0.8, 0.85);
      }
      d.plate([[16, 0], [28.5, 0], [28.5, 4.4], [16, 4.4]], 'frame', { sh: 0.8, hl: 0.6 });
      d.panel([[19, 0.2], [19, 4.2]], 0.6);
      d.panel([[22, 0.2], [22, 4.2]], 0.6);
      d.panel([[25, 0.2], [25, 4.2]], 0.6);
      d.plate([[25, -12], [44, -11.6], [46, -10], [46, -0.6], [44, 1], [25, 1]], 'plate', { sh: 2, hl: 0.9 });
      d.panel([[25.5, -5.6], [45.5, -5.6]]);
      d.plate([[-5.5, -12.4], [22, -13.2], [26.5, -10.6], [26.5, 1], [-4, 1], [-6.5, -6]], 'armor', { sh: 3.2, hl: 1.2, r: 0.8 });
      d.edge([[-5.2, -12.1], [22, -12.9]]);
      d.plate([[1, -10], [17, -10.4], [17, -7.4], [1, -7]], 'accent', { sh: 0.7, hl: 0.6, ink: 0.6 });
      d.bolt(-2, -3);
      d.bolt(22.5, -3);
      d.plate([[42.6, -12.8], [47.6, -12.4], [47.6, 1.6], [42.6, 2]], 'metal', { sh: 1, hl: 0.7 });
      d.slot([[46.4, -9.4], [48.2, -9.4], [48.2, -1.6], [46.4, -1.6]]);
    },
  },

  arc_lance: {
    bounds: { x0: -10, y0: -14, x1: 57, y1: 7 },
    muzzle: { x: 54, y: -5 },
    foregrip: { x: 13, y: -0.5 },
    glowColor: COLORS.electric,
    accent: ramp(0x14505a, 0x1f7f8c, 0x3fb3c0, 0x9fecf4),
    draw(d) {
      grip(d);
      d.plate([[24, -6.6], [46, -6.2], [46, -3.8], [24, -3.4]], 'metal', { sh: 0.6, hl: 0.5 });
      // Twin prongs (upper and its mirror about the barrel axis y = −5).
      const prong: [number, number][] = [[33, -9.5], [45, -12.6], [54.5, -11.2], [56, -9.4], [47, -8.4], [36, -6.6]];
      d.plate(prong, 'armor', { sh: 1.4, hl: 0.8, r: 0.5 });
      d.edge([[45, -12.4], [54.3, -11]]);
      d.plate(prong.map(([x, y]) => [x, -10 - y] as [number, number]), 'armor', { sh: 1.4, hl: 0.8, r: 0.5 });
      d.lampDisc(55, -9.8, 1, 1, 0.3);
      d.lampDisc(55, -0.2, 1, 1, 0.3);
      d.plate([[15, -10.4], [24.5, -10.8], [24.5, 0.6], [15, 0.2]], 'plate', { sh: 1.4, hl: 0.7 });
      ring(d, 25.5, -11.2, 1.2, 2.6, 1);
      ring(d, 29.5, -10.6, 0.6, 2.4, 0.95);
      ring(d, 33.3, -10, 0, 2.2, 0.9);
      d.plate([[-8.5, -9.2], [12, -10], [16, -7.4], [16, -1.2], [-6, -0.2], [-9.2, -4.2]], 'armor', { sh: 2.4, hl: 1, r: 0.6 });
      d.edge([[-8.2, -9], [12, -9.8]]);
      d.plate([[-6.5, -7.6], [9, -8.2], [10, -6], [-6.4, -5.6]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
      d.strip([[0, -3.2], [11.5, -3.6]], 0.9, 0.8);
    },
  },

  rail_driver: {
    bounds: { x0: -20, y0: -15, x1: 71, y1: 7 },
    muzzle: { x: 70, y: -6 },
    foregrip: { x: 22, y: 1.5 },
    glowColor: 0x8fd8ff,
    accent: ramp(0x4b2a6e, 0x6e44a0, 0x9a72cf, 0xd2bff2),
    draw(d) {
      d.plate([[-19, -7.4], [-6, -8.4], [-5, -1.2], [-11, -0.2], [-18.5, 4], [-20, -1]], 'plate', { sh: 1.5, hl: 0.8 });
      d.slot([[-16, -4.6], [-9, -5.2], [-9.4, -2.8], [-15.4, -0.6]]);
      grip(d);
      // Capacitor bank under the rails.
      d.plate([[6, -1.4], [31, -1.4], [30, 4.4], [7, 4.4]], 'plate', { sh: 1.2, hl: 0.7 });
      for (let i = 0; i < 4; i++) d.lampPoly([[9 + i * 5.4, 0.4], [12.6 + i * 5.4, 0.4], [12.6 + i * 5.4, 2.6], [9 + i * 5.4, 2.6]], 0.85, 0.4);
      // Twin rails with the charged gap between them.
      d.strip([[19, -6], [67.5, -6]], 1.6, 0.9);
      d.plate([[18, -11.2], [66, -10.8], [69.5, -9.4], [66, -8.2], [18, -8.4]], 'armor', { sh: 1.2, hl: 0.8, r: 0.4 });
      d.edge([[18.4, -11], [66, -10.6]]);
      d.plate([[18, -3.6], [66, -3.8], [69.5, -2.6], [66, -1.2], [18, -1]], 'armor', { sh: 1.2, hl: 0.6, r: 0.4 });
      for (const x of [30, 44, 58]) d.plate([[x, -12], [x + 2.6, -12], [x + 2.6, -0.2], [x, -0.2]], 'frame', { sh: 0.5, hl: 0.4, ink: 0.6 });
      d.plate([[-7.5, -10.4], [16, -10.8], [20.5, -8.2], [20.5, -1.2], [-6, -0.6], [-8.6, -5.4]], 'armor', { sh: 2.4, hl: 1, r: 0.6 });
      d.edge([[-7.2, -10.2], [16, -10.6]]);
      d.plate([[-6, -8.8], [13, -9.2], [14, -7], [-5.8, -6.6]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
      d.plate([[0, -10.6], [12, -10.8], [11, -14], [1, -14]], 'frame', { sh: 0.5, hl: 0.5, ink: 0.6 });
      d.lampPoly([[9.6, -13.2], [11.2, -13.2], [11.6, -11.4], [9.8, -11.4]], 1, 0.3);
    },
  },

  micro_rockets: {
    bounds: { x0: -11, y0: -20, x1: 38.5, y1: 7 },
    muzzle: { x: 38, y: -7.5 },
    foregrip: { x: 16.5, y: 4 },
    glowColor: COLORS.missile,
    accent: ramp(0x6e4a12, 0xb07a1c, 0xe0a740, 0xffd88c),
    draw(d) {
      grip(d);
      d.plate([[14, 0.6], [19.4, 0.6], [18.6, 6], [14.6, 6]], 'frame', { sh: 0.8, hl: 0.5 });
      d.plate([[2, -15.6], [10.5, -15.8], [9.4, -19], [3, -19]], 'frame', { sh: 0.5, hl: 0.5, ink: 0.6 });
      d.lampPoly([[7.6, -18.2], [9.2, -18.2], [9.6, -16.6], [7.8, -16.6]], 1, 0.3);
      d.plate([[-8.4, -16], [30, -16.2], [32, -14.4], [32, 1.2], [-8, 1.2], [-10.4, -7]], 'armor', { sh: 3.2, hl: 1.2, r: 0.8 });
      d.edge([[-8.2, -15.8], [30, -16]]);
      d.panel([[-9.6, -7.4], [31.6, -7.4]]);
      // Hazard chevrons on the side panel.
      for (const x of [4, 9, 14]) d.plate([[x, -13.6], [x + 2.6, -13.6], [x + 5, -10.2], [x + 2.4, -10.2]], 'accent', { sh: 0.4, hl: 0.4, ink: 0.4, r: 0.2 });
      d.bolt(-5.5, -12.5);
      d.bolt(-5.5, -2);
      d.bolt(27.5, -12.5);
      d.bolt(27.5, -2);
      // Front face with the 2×3 tube grid.
      d.plate([[30.5, -16.8], [37.6, -15.2], [37.6, 0.2], [30.5, 1.8]], 'plate', { sh: 1, hl: 0.7 });
      for (const y of [-12.4, -7.5, -2.6]) {
        for (const x of [32.6, 35.6]) {
          d.disc(x, y, 1.6, 'frame', { sh: 0.4, hl: 0.3, ink: 0.4 });
          d.lampDisc(x, y, 0.85, 0.9, 0);
        }
      }
    },
  },

  gravity_launcher: {
    bounds: { x0: -13, y0: -17, x1: 43, y1: 7.5 },
    muzzle: { x: 42.5, y: -5.5 },
    foregrip: { x: 21, y: 3.5 },
    glowColor: COLORS.gravity,
    accent: ramp(0x3a1d63, 0x6a3ea6, 0x9a6fda, 0xd2b8ff),
    draw(d) {
      d.plate([[-12.5, -8.4], [-4, -9.2], [-3, -1.5], [-11.5, 1.4]], 'plate', { sh: 1.5, hl: 0.8 });
      grip(d);
      d.plate([[27, -10.2], [40, -9.2], [42.4, -7.6], [42.4, -3.4], [40, -1.8], [27, -1.2]], 'plate', { sh: 1.4, hl: 0.8 });
      d.plate([[37.6, -10.8], [42.6, -10.2], [42.6, -0.8], [37.6, -0.2]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
      d.slot([[41.6, -7.6], [43.2, -7.6], [43.2, -3.4], [41.6, -3.4]]);
      d.plate([[-6.5, -10.2], [9, -11.2], [10.5, -1], [-5, 0], [-7.4, -5]], 'armor', { sh: 2.2, hl: 1, r: 0.6 });
      d.edge([[-6.2, -10], [9, -11]]);
      // Bulbous chamber.
      const bulb = new Path2D();
      bulb.ellipse(18.5, -5.8, 11.4, 10, 0, 0, Math.PI * 2);
      d.shape(bulb, 'armor', { sh: 3.4, hl: 1.3 });
      d.panel([[8, -10.6], [29, -10.6]], 0.7);
      d.panel([[8, -1], [29, -1]], 0.7);
      d.plate([[11, -15.4], [26, -15.4], [26.6, -13.6], [10.4, -13.6]], 'accent', { sh: 0.4, hl: 0.4, ink: 0.5, r: 0.3 });
      d.disc(18.5, -5.8, 5.8, 'frame', { sh: 1, hl: 0.7 });
      d.lampDisc(18.5, -5.8, 4, 1.1, 0.4);
      d.bolt(10.5, -5.8);
      d.bolt(26.5, -5.8);
    },
  },
};

export const WEAPON_SPECS: Readonly<Record<WeaponId, WeaponSpec>> = SPECS;
